import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PhoneTypeAttributes, CompanyAttributes } from '@albatrosdeveloper/ave-models-package';
import { ICreatePhoneType } from './interfaces/create-phone-type.interface';
import { IUpdatePhoneType } from './interfaces/update-phone-type.interface';
import { PhoneTypeService } from './phone-type.service.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableModule, MatRowDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { getCode } from 'app/utils/method';
import { Subscription } from 'rxjs';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { CompanyService } from '../../configuration/company/company.service.service';
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';
import { ConfigurationFiltersComponent } from '../../filters/configuration-filters/configuration-filters.component';
import { IRequestResult } from 'app/common/interfaces';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";



@Component({
  selector: 'app-phone-type',
  templateUrl: './phone-type.component.html',
  styleUrls: ['./phone-type.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    StatusFiltersComponent,
    FilterComponent,
    FilterSectionComponent,
    ConfigurationFiltersComponent,
    HoverMenuDirective
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
})
export class PhoneTypeComponent implements AfterViewInit, OnDestroy {
  getCode = getCode;

  displayedColumns: string[] = ['tipo', 'company', 'estado', 'acciones'];
  currentCreatePhoneType: ICreatePhoneType = this.initializeCreatePhoneType();
  currentUpdatePhoneType: PhoneTypeAttributes | undefined;

  dataSource: PhoneTypeAttributes[] = [];
  searchQuery: string = '';
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 0;

  companyList: CompanyAttributes[] = [];
  companyFilterList: CompanyAttributes[] = [];

  dialogRefAdd: MatDialogRef<any>;
  dialogRefEdit: MatDialogRef<any>;

  @ViewChild('editModal') editModal!: TemplateRef<any>;
  @ViewChild('addModal') addModal!: TemplateRef<any>;
  @ViewChild('deleteDialog') deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchInput') searchInput!: ElementRef;

  //BUSCADOR
  searchSubscription: Subscription;

  // Status Filter Properties
  selectedStatuses: number[] = [1, 0]; // Default to show both active and inactive

  // Company Filter Properties
  form: FormGroup;

  constructor(
    private readonly phoneTypeService: PhoneTypeService,
    private readonly companyService: CompanyService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    // Initialize form for filters
    this.form = this.fb.group({
      companyId: [null],
    });
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  private initializeCreatePhoneType(): ICreatePhoneType {
    return { type: '' };
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: 'ASC' | 'DESC' = this.sort.direction === 'asc' ? 'ASC' : 'DESC';

    this.phoneTypeService
      .findAllWithCount({
        currentPage: pageIndex,
        limitPerPage: this.pageSize,
        search: this.searchQuery,
        sort: sortField,
        sortDirection: sortDirection,
        status: this.selectedStatuses.length ? this.selectedStatuses : null,
      })
      .subscribe(data => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof PhoneTypeAttributes {
    const validSortFields: (keyof PhoneTypeAttributes)[] = ['type', 'statusId'];
    return validSortFields.includes(this.sort.active as keyof PhoneTypeAttributes)
      ? (this.sort.active as keyof PhoneTypeAttributes)
      : 'type';
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(term => {
      this.searchQuery = term;
      if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
        this.paginator.pageIndex = 0;
        this.findAllWithCount();
      }
    });
  }

  trackByFn(index: number, item: PhoneTypeAttributes): string {
    return item._id.toString();
  }

  openCreateModal(): void {
    this.currentCreatePhoneType = this.initializeCreatePhoneType();
    this.dialogRefAdd = this.dialog.open(this.addModal, {
      panelClass: 'form-modal',
      disableClose: true,
    });
    // this.dialogRefAdd.afterClosed().subscribe((result) => result && this.createPhoneType());
    this.dialogRefAdd.afterClosed().subscribe(result => {
      result;
    });
  }

  openEditModal(phoneType: PhoneTypeAttributes): void {
    this.currentUpdatePhoneType = { ...phoneType };
    this.dialogRefEdit = this.dialog.open(this.editModal, {
      panelClass: 'form-modal',
      disableClose: true,
    });
    // this.dialogRefEdit.afterClosed().subscribe((result) => result && this.updatePhoneType(phoneType._id.toString()));
    this.dialogRefEdit.afterClosed().subscribe(result => {
      result;
    });
  }

  createPhoneType(): void {
    this.phoneTypeService.create(this.currentCreatePhoneType).subscribe({
      next: () => {
        this.snackBar.open('Registro creado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
        this.dialogRefAdd.close();
      },
      error: err => {
        this.snackBar.open(err.error.message || 'Error al crear el registro', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  updatePhoneType(id: string): void {
    if (this.currentUpdatePhoneType) {
      const updatedPhoneType: IUpdatePhoneType = { ...this.currentUpdatePhoneType };

      this.phoneTypeService.update(id, updatedPhoneType).subscribe({
        next: () => {
          this.snackBar.open('Registro actualizado con éxito', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
          this.dialogRefEdit.close();
        },
        error: err => {
          this.snackBar.open(err.error.message || 'Error al actualizar el registro', 'Cerrar', {
            duration: 3000,
          });
        },
      });
    }
  }

  onStatusChange(event: boolean, phoneType: PhoneTypeAttributes): void {
    const originalStatusId = phoneType.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedPhoneType = { ...phoneType, statusId: updatedStatusId };

    this.phoneTypeService.update(phoneType._id.toString(), updatedPhoneType).subscribe({
      next: () => {
        this.findAllWithCount();
      },
      error: () => {
        phoneType.statusId = originalStatusId; // Volver al estado original si falla
        this.snackBar.open('Error al actualizar el estado.', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
      },
    });
  }

  openDeleteDialog(phoneType: PhoneTypeAttributes): void {
    this.dialog
      .open(this.deleteDialog, { data: phoneType, panelClass: 'delete-modal', disableClose: true })
      .afterClosed()
      .subscribe(result => result && this.delete(phoneType._id.toString()));
  }

  delete(id: string): void {
    this.phoneTypeService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
    });
  }

  // Status Filter Methods
  applyStatusFilter(): void {
    this.paginator.pageIndex = 0; // Reset to first page when filtering
    this.findAllWithCount();
  }

  // Company Filter Methods
  onFilterEvent(event: any): void {
    this.paginator.pageIndex = 0; // Reset to first page when filtering
    this.findAllWithCount();
  }

  onCleanEvent(event: any): void {
    this.form.patchValue({
      companyId: null,
    });
    this.paginator.pageIndex = 0;
    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
